import apiService from '../utils/apiInterceptor';
import config from '../config';

/**
 * Service for handling user messages and contact forms
 */
const messageService = {
  /**
   * Get messages for the current user with pagination and filters
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Messages data
   */
  getMessages: async (params = {}) => {
    try {
      const response = await apiService.get('/messages', { params });
      return response;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  /**
   * Get a specific message by ID
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Message data
   */
  getMessage: async (messageId) => {
    try {
      const response = await apiService.get(`/messages/${messageId}`);
      return response;
    } catch (error) {
      console.error('Error fetching message:', error);
      throw error;
    }
  },

  /**
   * Mark a message as read
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Response data
   */
  markAsRead: async (messageId) => {
    try {
      const response = await apiService.post(`/messages/${messageId}/read`);
      return response;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },

  /**
   * Mark all messages as read
   * @returns {Promise<Object>} Response data
   */
  markAllAsRead: async () => {
    try {
      const response = await apiService.post('/messages/read-all');
      return response;
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      throw error;
    }
  },

  /**
   * Delete a message
   * @param {number} messageId - Message ID
   * @returns {Promise<Object>} Response data
   */
  deleteMessage: async (messageId) => {
    try {
      const response = await apiService.delete(`/messages/${messageId}`);
      return response;
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  /**
   * Send a reply to a message
   * @param {number} messageId - Message ID to reply to
   * @param {string} message - Reply message content
   * @returns {Promise<Object>} Response data
   */
  replyToMessage: async (messageId, message) => {
    try {
      const response = await apiService.post(`/messages/${messageId}/reply`, { message });
      return response;
    } catch (error) {
      console.error('Error replying to message:', error);
      throw error;
    }
  },

  /**
   * Send a new message (admin only)
   * @param {FormData} formData - Message data including recipients and attachments
   * @returns {Promise<Object>} Response data
   */
  sendMessage: async (formData) => {
    try {
      const response = await apiService.post('/admin/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  /**
   * Get sent messages (admin only)
   * @param {Object} params - Pagination and filter parameters
   * @returns {Promise<Object>} Sent messages data
   */
  getSentMessages: async (params = {}) => {
    try {
      const response = await apiService.get('/admin/messages/sent', { params });
      return response;
    } catch (error) {
      console.error('Error fetching sent messages:', error);
      throw error;
    }
  },

  getInboxMessages: async (params = {}) => {
    return apiService.get('/admin/messages/inbox', { params });
  },

  /**
   * Get unread messages count
   * @returns {Promise<Object>} Response data with count
   */
  getUnreadCount: async () => {
    try {
      const response = await apiService.get('/messages/unread-count-m');
      return response;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  // ====== CONTACT FORM METHODS ======

  /**
   * Get contact forms with pagination and filters (admin only)
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Contact forms data with stats
   */
  getContactForms: async (params = {}) => {
    try {
      const response = await apiService.get('/contact-forms', { params });
      return response;
    } catch (error) {
      console.error('Error fetching contact forms:', error);
      throw error;
    }
  },
 
 
  /**
   * Get a specific contact form by ID (admin only)
   * @param {number} contactFormId - Contact form ID
   * @returns {Promise<Object>} Contact form data
   */
  getContactForm: async (contactFormId) => {
    try {
      const response = await apiService.get(`/contact-forms/${contactFormId}`);
      return response;
    } catch (error) {
      console.error('Error fetching contact form:', error);
      throw error;
    }
  },

  /**
   * Submit a new contact form (public endpoint)
   * @param {Object} formData - Contact form data
   * @returns {Promise<Object>} Response data
   */
  submitContactForm: async (formData) => {
    try {
      const response = await apiService.post('/contact-forms', formData);
      return response;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  },

  /**
   * Update contact form status and notes (admin only)
   * @param {number} contactFormId - Contact form ID
   * @param {Object} updateData - Update data (status, admin_notes, etc.)
   * @returns {Promise<Object>} Response data
   */
  updateContactForm: async (contactFormId, updateData) => {
    try {
      const response = await apiService.put(`/contact-forms/${contactFormId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating contact form:', error);
      throw error;
    }
  },

  /**
   * Delete a contact form (admin only)
   * @param {number} contactFormId - Contact form ID
   * @returns {Promise<Object>} Response data
   */
  deleteContactForm: async (contactFormId) => {
    try {
      const response = await apiService.delete(`/contact-forms/${contactFormId}`);
      return response;
    } catch (error) {
      console.error('Error deleting contact form:', error);
      throw error;
    }
  },

  /**
   * Bulk update contact forms status (admin only)
   * @param {Array} contactFormIds - Array of contact form IDs
   * @param {Object} updateData - Update data (status, admin_notes, etc.)
   * @returns {Promise<Object>} Response data
   */
  bulkUpdateContactForms: async (contactFormIds, updateData) => {
    try {
      const response = await apiService.post('/contact-forms/bulk-update', {
        ids: contactFormIds,
        ...updateData
      });
      return response;
    } catch (error) {
      console.error('Error bulk updating contact forms:', error);
      throw error;
    }
  },

  /**
   * Assign contact form to a user (admin only)
   * @param {number} contactFormId - Contact form ID
   * @param {number} userId - User ID to assign to
   * @returns {Promise<Object>} Response data
   */
  assignContactForm: async (contactFormId, userId) => {
    try {
      const response = await apiService.post(`/contact-forms/${contactFormId}/assign`, {
        user_id: userId
      });
      return response;
    } catch (error) {
      console.error('Error assigning contact form:', error);
      throw error;
    }
  },

  /**
   * Add notes to a contact form (admin only)
   * @param {number} contactFormId - Contact form ID
   * @param {string} notes - Notes to add
   * @returns {Promise<Object>} Response data
   */
  addContactFormNotes: async (contactFormId, notes) => {
    try {
      const response = await apiService.post(`/contact-forms/${contactFormId}/notes`, {
        notes
      });
      return response;
    } catch (error) {
      console.error('Error adding notes to contact form:', error);
      throw error;
    }
  },

  /**
   * Get contact form statistics (admin only)
   * @param {Object} params - Filter parameters for statistics
   * @returns {Promise<Object>} Statistics data
   */
  getContactFormStats: async (params = {}) => {
    try {
      const response = await apiService.get('/contact-forms/stats', { params });
      return response;
    } catch (error) {
      console.error('Error fetching contact form statistics:', error);
      throw error;
    }
  },

  /**
   * Export contact forms to CSV/Excel (admin only)
   * @param {Object} params - Filter parameters for export
   * @returns {Promise<Blob>} File blob for download
   */
  exportContactForms: async (params = {}) => {
    try {
      const response = await apiService.get('/contact-forms/export', {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Error exporting contact forms:', error);
      throw error;
    }
  },

  /**
   * Get contact form activity/history (admin only)
   * @param {number} contactFormId - Contact form ID
   * @returns {Promise<Object>} Activity history data
   */
  getContactFormActivity: async (contactFormId) => {
    try {
      const response = await apiService.get(`/contact-forms/${contactFormId}/activity`);
      return response;
    } catch (error) {
      console.error('Error fetching contact form activity:', error);
      throw error;
    }
  },

  /**
   * Mark contact form as contacted (admin only)
   * @param {number} contactFormId - Contact form ID
   * @param {string} contactMethod - Method of contact (email, phone, etc.)
   * @param {string} notes - Optional notes about the contact
   * @returns {Promise<Object>} Response data
   */
  markContactFormAsContacted: async (contactFormId, contactMethod, notes = '') => {
    try {
      const response = await apiService.post(`/contact-forms/${contactFormId}/contacted`, {
        contact_method: contactMethod,
        notes,
        contacted_at: new Date().toISOString()
      });
      return response;
    } catch (error) {
      console.error('Error marking contact form as contacted:', error);
      throw error;
    }
  },

  /**
   * Get contact forms dashboard data (admin only)
   * @returns {Promise<Object>} Dashboard data with recent forms, stats, etc.
   */
  getContactFormsDashboard: async () => {
    try {
      const response = await apiService.get('/contact-forms/dashboard');
      return response;
    } catch (error) {
      console.error('Error fetching contact forms dashboard:', error);
      throw error;
    }
  }
};

export default messageService;
